const url = process.env.VUE_APP_API_URL;

// Shared fetch helper. Resolves with the parsed JSON, or throws { response, res }
// the same way the other store modules do.
async function request(rootState, path, { method = "GET", body } = {}) {
  const response = await fetch(`${url}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${rootState.userToken}`,
    },
    body,
  });

  const res = await response.json();

  if (!response.ok) {
    throw { response, res };
  }

  return res;
}

// Maps a failed request to the app's existing error mutations.
function handleError(commit, error) {
  const status = error && error.response ? error.response.status : 0;
  const res = (error && error.res) || {};
  const detail =
    (res.messages && res.messages.error) ||
    "An error occurred, please try again!";

  commit("setError", "Error"); // Decoy error to detect success

  switch (status) {
    case 401:
      commit("setError", "Unauthorized", { root: true });
      break;
    case 403: // failForbidden, e.g. category still has blogs
    case 404: // failNotFound
      commit("setPopUpError", detail, { root: true });
      break;
    case 417:
      commit("setPopUpError", res.error || detail, { root: true });
      break;
    case 422:
      commit("setErrors", res.messages || {});
      break;
    default:
      commit("setRefreshError", "An error occurred, please try again!", {
        root: true,
      });
      break;
  }
}

async function runApproval(commit, rootState, path) {
  commit("setSaving", true);
  commit("setError", null);
  commit("setErrors", {});

  try {
    await request(rootState, path, { method: "POST" });
    return true;
  } catch (error) {
    handleError(commit, error);
    return false;
  } finally {
    commit("setSaving", false);
  }
}

export default {
  namespaced: true,

  state: {
    items: [],
    total: 0,
    blog: null,
    categories: [],
    loading: false,
    saving: false,
    uploading: false,
    error: null,
    errors: {},
  },

  mutations: {
    setItems(state, val) {
      state.items = val;
    },
    setTotal(state, val) {
      state.total = val;
    },
    setBlog(state, val) {
      state.blog = val;
    },
    setCategories(state, val) {
      state.categories = val;
    },
    setLoading(state, val) {
      state.loading = val;
    },
    setSaving(state, val) {
      state.saving = val;
    },
    setUploading(state, val) {
      state.uploading = val;
    },
    setError(state, val) {
      state.error = val;
    },
    setErrors(state, val) {
      state.errors = val;
    },
  },

  actions: {
    // ---------- Blogs ----------
    async getBlogs({ commit, rootState }, query) {
      commit("setLoading", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        const res = await request(rootState, `blogs?${query}`);

        commit("setItems", res.items);
        commit("setTotal", res.total);
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setLoading", false);
      }
    },

    async getBlog({ commit, rootState }, id) {
      commit("setLoading", true);
      commit("setError", null);
      commit("setErrors", {});
      commit("setBlog", null);

      try {
        const res = await request(rootState, `blogs/show/${id}`);

        commit("setBlog", res);
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setLoading", false);
      }
    },

    async createBlog({ commit, rootState }, data) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        await request(rootState, "blogs/create", {
          method: "POST",
          body: data,
        });
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setSaving", false);
      }
    },

    // payload: { id, data }
    async updateBlog({ commit, rootState }, { id, data }) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        await request(rootState, `blogs/update/${id}`, {
          method: "POST",
          body: data,
        });
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setSaving", false);
      }
    },

    async deleteBlog({ commit, rootState }, id) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        await request(rootState, `blogs/delete/${id}`, { method: "POST" });
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setSaving", false);
      }
    },

    // Approve (or remove the approval of) a blog. Resolves true on success.
    async approveBlog({ commit, rootState }, id) {
      return runApproval(commit, rootState, `blogs/approve/${id}`);
    },

    async unapproveBlog({ commit, rootState }, id) {
      return runApproval(commit, rootState, `blogs/unapprove/${id}`);
    },

    // ---------- Categories ----------
    async getCategories({ commit, rootState }) {
      commit("setLoading", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        const res = await request(rootState, "blog_categories");

        commit("setCategories", res);
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setLoading", false);
      }
    },

    // Resolves with { message, id } on success, or null when it failed.
    async createCategory({ commit, rootState }, data) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        return await request(rootState, "blog_categories/create", {
          method: "POST",
          body: data,
        });
      } catch (error) {
        handleError(commit, error);
        return null;
      } finally {
        commit("setSaving", false);
      }
    },

    // payload: { id, data }
    async updateCategory({ commit, rootState }, { id, data }) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        await request(rootState, `blog_categories/update/${id}`, {
          method: "POST",
          body: data,
        });
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setSaving", false);
      }
    },

    async deleteCategory({ commit, rootState }, id) {
      commit("setSaving", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        await request(rootState, `blog_categories/delete/${id}`, {
          method: "POST",
        });
      } catch (error) {
        handleError(commit, error);
      } finally {
        commit("setSaving", false);
      }
    },

    // ---------- Files ----------
    // payload: { file, name }. Resolves with { file_name, original_name, mime_type, size, url },
    // or null when the upload failed (field messages are in state.errors.file).
    async uploadFile({ commit, rootState }, { file, name }) {
      commit("setUploading", true);
      commit("setError", null);
      commit("setErrors", {});

      try {
        const body = new FormData();
        body.append("file", file, name || file.name);

        return await request(rootState, "blog_files/upload", {
          method: "POST",
          body,
        });
      } catch (error) {
        handleError(commit, error);
        return null;
      } finally {
        commit("setUploading", false);
      }
    },

    // Cleanup call (old cover images, abandoned uploads). Failures are ignored on
    // purpose and it does not touch the shared error state.
    async deleteFile({ rootState }, fileName) {
      try {
        const body = new FormData();
        body.append("file_name", fileName);

        await request(rootState, "blog_files/delete", { method: "POST", body });
        return true;
      } catch (error) {
        return false;
      }
    },
  },
};
